/**
 * Balances as people see them. The ledger keeps fractions of a cent (splits are exact), and a
 * leftover under half a cent shows as $0.00. Totals on screen are built from what each row
 * shows, never from the raw leftovers, so a header always equals the sum of its rows and no
 * phantom $0.01 appears in either direction.
 */
import { formatCents, microToCents, type Cents, type Micro } from "./money.ts";

/** One balance as shown: whole cents, half a cent rounding away from zero. */
export function shownCents(m: Micro): Cents {
  return microToCents(m) || 0;
}

/** A total made of several shown parts (one person across groups): the sum of the parts. */
export function shownSum(parts: readonly Micro[]): Cents {
  return parts.reduce((s, m) => s + shownCents(m), 0);
}

/** Header totals from the rows' shown cents. Positive = owed to you; negative = you owe. */
export function shownTotals(rows: readonly Cents[]): { owedToYouCents: Cents; youOweCents: Cents } {
  let owed = 0;
  let owe = 0;
  for (const c of rows) {
    if (c > 0) owed += c;
    else if (c < 0) owe -= c;
  }
  return { owedToYouCents: owed, youOweCents: owe };
}

/**
 * The one way a balance is said, on every screen and for everyone, from the viewer's side:
 * positive = they owe you. "Owes you $12.00", "You owe $3.50" or "Paid up".
 */
export function owesText(cents: Cents): string {
  if (cents > 0) return `Owes you ${formatCents(cents)}`;
  if (cents < 0) return `You owe ${formatCents(-cents)}`;
  return "Paid up";
}

/** The short caption under a balance's amount: "owes you", "you owe" or "paid up". */
export function owesCaption(cents: Cents): string {
  return cents > 0 ? "owes you" : cents < 0 ? "you owe" : "paid up";
}
